<template>
  <nav>
    <v-app-bar flat color="#f5f5f5" height="70" class="px-md-6 px-lg-8 border-bottom">
      <!-- Logo -->
      <v-toolbar-title class="ml-2 ml-md-0 d-flex align-center" style="cursor: pointer" @click="$router.push('/home').catch(()=>{})">
        <v-img src="../assets/logo grey.svg" max-width="50" contain class="mr-2"></v-img>
        <span class="font-weight-bold ml-1" style="color: #333; font-size: 22px; letter-spacing: -0.5px">
          jangu<span style="color: #f69227">Ask</span>
        </span>
      </v-toolbar-title>
      
      <v-spacer></v-spacer>

      <!-- Search Bar (Desktop) -->
      <div class="hidden-sm-and-down mx-4 flex-grow-1" style="max-width: 400px">
        <v-text-field
          dense
          outlined
          rounded
          hide-details
          placeholder="Search for courses, questions, or people..."
          prepend-inner-icon="mdi-magnify"
          color="#f69227"
          class="elevation-0 bg-white"
        ></v-text-field>
      </div>

      <v-spacer class="hidden-sm-and-down"></v-spacer>

      <!-- Navigation Links -->
      <div class="hidden-sm-and-down d-flex align-center">
        <v-btn
          text
          v-for="link in links"
          :key="link.text"
          router
          :to="link.route"
          class="text-capitalize mx-1 nav-btn font-weight-medium"
          :class="{'nav-btn-active': $route.path.startsWith(link.route)}"
          :color="$route.path.startsWith(link.route) ? '#f69227' : 'grey darken-2'"
        >
          <v-icon left small class="mr-1">{{ link.icon }}</v-icon>
          {{ link.text }}
        </v-btn>
      </div>

      <v-spacer class="hidden-md-and-up"></v-spacer>

      <!-- Action Buttons & Profile Dropdown -->
      <div class="d-flex align-center">
        <!-- Add Button -->
        <v-btn icon color="grey darken-2" class="mx-1 hover-scale hidden-xs-only">
          <v-icon>mdi-plus-circle-outline</v-icon>
        </v-btn>

        <!-- Messages -->
        <v-btn icon to="/groupchat" color="grey darken-2" class="mx-1 hover-scale hidden-xs-only position-relative">
          <v-badge content="3" color="#f69227" overlap bordered>
            <v-icon>mdi-message-text-outline</v-icon>
          </v-badge>
        </v-btn>

        <!-- Notifications -->
        <v-btn icon to="/notifications" color="grey darken-2" class="mx-1 hover-scale position-relative">
          <v-badge content="5" color="error" overlap bordered dot>
            <v-icon>mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>

        <!-- User Profile Dropdown -->
        <v-menu offset-y rounded="lg" transition="slide-y-transition" bottom right>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" class="ml-2 hover-scale" width="40" height="40">
              <v-avatar size="36" class="elevation-1" style="border: 2px solid white">
                <img :src="$root.store.getUserAvatar()" alt="Profile" />
              </v-avatar>
            </v-btn>
          </template>
          <v-list width="240" class="pa-2">
            <v-list-item>
              <v-list-item-avatar size="45">
                <img :src="$root.store.getUserAvatar()" alt="Profile" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold" style="font-size: 15px">{{ $root.storeState.currentUser.name || 'Student' }}</v-list-item-title>
                <v-list-item-subtitle style="font-size: 12px">{{ $root.storeState.currentUser.school || 'Not specified' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="my-2"></v-divider>
            <v-list-item link to="/settings" class="rounded-lg mb-1 hover-item">
              <v-list-item-icon class="mr-3"><v-icon small color="grey darken-2">mdi-cog-outline</v-icon></v-list-item-icon>
              <v-list-item-title style="font-size: 14px" class="font-weight-medium">Settings & Privacy</v-list-item-title>
            </v-list-item>
            <v-list-item link class="rounded-lg mb-1 hover-item" @click="notAvailable('Help Center')">
              <v-list-item-icon class="mr-3"><v-icon small color="grey darken-2">mdi-help-circle-outline</v-icon></v-list-item-icon>
              <v-list-item-title style="font-size: 14px" class="font-weight-medium">Help & Support</v-list-item-title>
            </v-list-item>
            <v-divider class="my-1"></v-divider>
            <v-list-item link @click="logout" class="rounded-lg hover-item">
              <v-list-item-icon class="mr-3"><v-icon small color="error">mdi-logout</v-icon></v-list-item-icon>
              <v-list-item-title style="font-size: 14px; color: #ef5350" class="font-weight-medium">Sign Out</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Mobile Menu Toggle -->
        <v-btn icon color="grey darken-2" class="ml-1 hidden-md-and-up hover-scale" @click="drawer = !drawer">
          <v-icon>mdi-menu</v-icon>
        </v-btn>
      </div>
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer v-model="drawer" app right temporary width="280">
      <v-list-item class="px-4 py-4" style="background-color: #f5f5f5">
        <v-list-item-avatar size="50" class="elevation-1 border-white">
          <img :src="$root.store.getUserAvatar()" alt="Profile" />
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="font-weight-bold" style="font-size: 16px">{{ $root.storeState.currentUser.name || 'Student' }}</v-list-item-title>
          <v-list-item-subtitle class="mt-1" style="font-size: 13px">{{ $root.storeState.currentUser.school || 'Not specified' }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <v-list class="px-2" nav dense>
        <v-list-item class="mb-2 mt-1">
          <v-text-field
            dense
            outlined
            rounded
            hide-details
            placeholder="Search..."
            prepend-inner-icon="mdi-magnify"
            color="#f69227"
            class="bg-white"
          ></v-text-field>
        </v-list-item>
        <v-list-item v-for="link in links" :key="link.text" router :to="link.route" class="rounded-lg mb-1 mt-2">
          <v-list-item-icon class="mr-3">
            <v-icon :color="$route.path.startsWith(link.route) ? '#f69227' : 'grey darken-1'">{{ link.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title 
              :class="{'font-weight-bold orange--text text--darken-1': $route.path.startsWith(link.route), 'font-weight-medium': !$route.path.startsWith(link.route)}" 
              style="font-size: 15px"
            >
              {{ link.text }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="my-3"></v-divider>
        
        <v-list-item link to="/settings" class="rounded-lg mb-1">
          <v-list-item-icon class="mr-3"><v-icon color="grey darken-1">mdi-cog-outline</v-icon></v-list-item-icon>
          <v-list-item-content><v-list-item-title class="font-weight-medium" style="font-size: 15px">Settings</v-list-item-title></v-list-item-content>
        </v-list-item>
        
        <v-list-item link @click="logout" class="rounded-lg">
          <v-list-item-icon class="mr-3"><v-icon color="error">mdi-logout</v-icon></v-list-item-icon>
          <v-list-item-content><v-list-item-title class="font-weight-medium" style="font-size: 15px; color: #ef5350">Sign Out</v-list-item-title></v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </nav>
</template>

<script>
export default {
  data() {
    return {
      drawer: false,
      links: [
        { icon: "mdi-home-variant-outline", text: "Home", route: "/home" },
        { icon: "mdi-forum-outline", text: "Discussion", route: "/discussion" },
        { icon: "mdi-account-group-outline", text: "Group", route: "/group" },
        { icon: "mdi-book-open-page-variant-outline", text: "Yearbook", route: "/yearbook" },
        { icon: "mdi-library-video", text: "Lectures", route: "/lectureshare" },
        { icon: "mdi-brain", text: "Quiz", route: "/quizebank" },
      ],
    };
  },
  methods: {
    logout() {
      this.$root.store.logout();
      this.$root.showSnackbar("You have been signed out.", "#333", "mdi-check-circle");
      this.$router.push('/login');
    },
    notAvailable(feature) {
      this.$root.showSnackbar(`${feature} is not available at the moment.`, "#ef5350", "mdi-alert-circle");
    }
  }
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid rgba(0,0,0,0.05) !important;
}
.bg-white {
  background-color: white !important;
}
.border-white {
  border: 2px solid white;
}
.nav-btn {
  border-radius: 8px;
  letter-spacing: 0;
  transition: all 0.3s ease;
}
.nav-btn:hover {
  background-color: rgba(246, 146, 39, 0.08) !important;
  color: #f69227 !important;
}
.nav-btn-active {
  background-color: rgba(246, 146, 39, 0.1) !important;
}
.hover-item {
  transition: background-color 0.2s ease;
}
.hover-item:hover {
  background-color: #f8f8f8;
}
</style>
